import { useEffect, useState } from "react";
import { useBreakpoint } from "../../../hooks";

/**
 * The type of view to render based on device characteristics.
 */
export type AdaptiveView = "mobile" | "desktop";

/**
 * Return type for the useAdaptiveView hook.
 */
export interface UseAdaptiveViewReturn {
  /**
   * The current adaptive view type.
   */
  view: AdaptiveView;
  /**
   * True when the device should use mobile-optimized UI (Dialog).
   */
  isMobile: boolean;
  /**
   * True when the device should use desktop-optimized UI (Popover).
   */
  isDesktop: boolean;
}

/**
 * Detects whether the primary pointer is coarse (touch) using CSS media query.
 * Returns false during SSR or when matchMedia is unavailable.
 */
function detectCoarsePointer(): boolean {
  if (typeof window === "undefined" || !window.matchMedia) {
    return false;
  }
  return window.matchMedia("(pointer: coarse)").matches;
}

/**
 * Custom hook for detecting whether to use mobile or desktop UI patterns.
 *
 * This hook determines the optimal UI pattern (e.g., Dialog vs Popover) by combining:
 * - Screen size via useBreakpoint (viewport width)
 * - Pointer precision via CSS media query (pointer: coarse)
 *
 * A device is considered "mobile" when it has both:
 * - A small screen (below md breakpoint, < 768px)
 * - A coarse pointer (touch-primary input)
 *
 * This approach correctly identifies:
 * - Mobile phones → mobile view (Dialog)
 * - iPads/tablets → desktop view (Popover) due to larger screens
 * - Touch laptops → desktop view (Popover) due to fine pointer primary
 * - Desktop → desktop view (Popover)
 *
 * @returns Object containing view type and boolean flags for mobile/desktop
 *
 * @example
 * const { isMobile } = useAdaptiveView();
 *
 * return isMobile ? (
 *   <Dialog>{children}</Dialog>
 * ) : (
 *   <Popover>{children}</Popover>
 * );
 */
export function useAdaptiveView(): UseAdaptiveViewReturn {
  const breakpoint = useBreakpoint();
  const [hasCoarsePointer, setHasCoarsePointer] = useState(detectCoarsePointer);

  useEffect(() => {
    setHasCoarsePointer(detectCoarsePointer());

    // Listen for pointer capability changes (e.g., connecting/disconnecting a mouse)
    const mediaQuery = window.matchMedia("(pointer: coarse)");
    const handleChange = (e: MediaQueryListEvent) => {
      setHasCoarsePointer(e.matches);
    };

    mediaQuery.addEventListener("change", handleChange);
    return () => mediaQuery.removeEventListener("change", handleChange);
  }, []);

  const isSmallScreen = !breakpoint?.md;
  const isMobile = isSmallScreen && hasCoarsePointer;

  return {
    view: isMobile ? "mobile" : "desktop",
    isMobile,
    isDesktop: !isMobile,
  };
}
